/**
 * Top-level React error boundary.
 *
 * Catches render-time exceptions anywhere below it and renders a friendly
 * fallback with a "Reload" button instead of letting the whole app go blank.
 * The previous build crashed silently with a blank `#root` whenever a leaf
 * component (e.g. MessageList virtualizer) threw — this boundary gives the
 * user a way back and surfaces the failure to the console.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (err: Error, reset: () => void) => ReactNode;
}

interface State {
  err: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', err, info);
  }

  reset = () => this.setState({ err: null });

  render() {
    if (this.state.err) {
      if (this.props.fallback) return this.props.fallback(this.state.err, this.reset);
      return (
        <div
          role="alert"
          className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center"
        >
          <span aria-hidden="true" className="text-5xl">⚠️</span>
          <h1 className="text-base font-semibold">Something went wrong</h1>
          <p className="max-w-md text-xs text-ink-500 dark:text-ink-300">
            {this.state.err.message || 'Unexpected error'}
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              this.reset();
              // Hard reload as a last-resort recovery — boundary state alone
              // won't recover from a module-level throw.
              window.location.reload();
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
